import { useEffect, useRef } from 'react';
import '../styles/about.css';

/** The five service steps that spell GUEST. */
const STEPS = [
  { letter: 'G', word: 'Greet', desc: 'Welcome them warmly' },
  { letter: 'U', word: 'Understand', desc: 'Learn what they love' },
  { letter: 'E', word: 'Enthuse', desc: 'Share our expertise' },
  { letter: 'S', word: 'Sell', desc: 'Find the right bottle' },
  { letter: 'T', word: 'Thank', desc: 'Leave them looking forward' },
] as const;

/** O'Briens values, official wording + Tabler icons. */
const VALUES = [
  { icon: 'ti-award', lead: 'We are', trait: 'Expert' },
  { icon: 'ti-seeding', lead: 'We are', trait: 'Authentic' },
  { icon: 'ti-users-group', lead: 'We are', trait: 'One team' },
  { icon: 'ti-diamond', lead: 'We deliver', trait: 'Value' },
] as const;

interface AboutPageProps {
  onClose: () => void;
}

/**
 * Hidden "About" easter egg (route /about), opened by clicking the sidebar logo.
 * Static content — no data or auth. Rendered as a centred panel over the current
 * screen; scrim click, the X, or Escape close it.
 */
export function AboutPage({ onClose }: AboutPageProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Lock background scroll while the panel is open.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return (
    <div className="about-scrim" onClick={onClose}>
      <div
        className="about-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="about-hero-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="about-head">
          <span className="about-head-mark" aria-hidden="true">O</span>
          <span className="about-head-label">About</span>
          <button
            ref={closeRef}
            type="button"
            className="about-close"
            onClick={onClose}
            aria-label="Close"
          >
            <i className="ti ti-x" aria-hidden="true" />
          </button>
        </header>

        <section className="about-hero">
          <h1 className="about-hero-title" id="about-hero-title">How we serve</h1>
          <p className="about-hero-sub">
            Every great interaction follows five steps. It&rsquo;s the reason this tool is
            called <span className="about-guest">GUEST</span>.
          </p>
        </section>

        <ol className="about-steps">
          {STEPS.map((s) => (
            <li className="about-step" key={s.letter}>
              <span className="about-step-letter">{s.letter}</span>
              <span className="about-step-word">{s.word}</span>
              <span className="about-step-desc">{s.desc}</span>
            </li>
          ))}
        </ol>

        <p className="about-values-label">Built on our values</p>
        <ul className="about-values">
          {VALUES.map((v) => (
            <li className="about-value" key={v.trait}>
              <span className="about-value-icon">
                <i className={`ti ${v.icon}`} aria-hidden="true" />
              </span>
              <span className="about-value-label">
                {v.lead} <strong>{v.trait}</strong>
              </span>
            </li>
          ))}
        </ul>

        <hr className="about-divider" />

        <p className="about-closing">
          GUEST helps us work smarter &mdash; meeting our customers where they need us, and
          delivering unrivalled service, experience and value.
        </p>
      </div>
    </div>
  );
}
